import { writable } from 'svelte/store';
import type { Writable } from 'svelte/store';
import { getContext, setContext } from 'svelte';
import { StoreKeys } from './StoresKeys';
import type { ExtraChargeType } from '$lib/constants';
import type { RoofData } from '$lib/types/irradiation';

export const enum CharacteristicsConsumptionEnum {
	DAY = 'Day',
	NIGHT = 'Night',
	OPTIMIZED = 'Optimized',
	EMPTY = ''
}

// Stromverbrauch
export type ElectricityConsumption = {
	currentPrice: number;
	duration: number;
	inflation: number;
	priceIncreaseRate: number;
	electricityConsumption: number;
};

// Verbrauchscharakteristik
export type CharacteristicsConsumption = {
	characteristicsConsumption: CharacteristicsConsumptionEnum;
};

// Dachfläche(n)
export type RoofSurface = {
	roofOrientation: number;
	roofSlope: number;
	solarInstallation: number;
	displayModule?: number;
	index: number;
};

export type RoofSurfaces = {
	modules: Array<RoofSurface & object>;
};

// Location, Insolation
export type Insolation = {
	plz?: string;
	location?: string;
	state?: string;
	source?: string;
	insolation: number;
	roofData?: RoofData;
	roofImageUrl?: string;
	irradiationArray?: number[];
	selectedIrradiationIndex?: number; 
	maxSunshineHoursPerYear?: number;
};

// Module Component
export type ModuleComponent = {
	moduleType: number | undefined;
	batterySystem: number | undefined;
};

// Addition Options
export type AdditionOptions = {
	name: string;
	type: string;
	amount: number;
	unitPrice: number;
	id?: number;
	totalPrice?: number;
	pdfText: string;
	roofIndex?: number | 'all';
};

export type Configurator = {
	sessionId: number;
	electricityConsumption: ElectricityConsumption;
	characteristicsConsumption: CharacteristicsConsumption;
	roofSurfaces: RoofSurfaces;
	insolation: Insolation;
	moduleComponent: ModuleComponent;
	additionOptions: Array<AdditionOptions>;
	acConnection: number;
	acConnectionPrice: number;
	dimensionFactorPV: number;
	dimensionFactorBattery: number;
	pvSize: number;
	amountOfModule: number;
	amountOfBattery: number;
	hasTax: boolean;
	discount: number;
	fuelPrice: number;
	heatPumpConsume: number;
	eCarConsume: number;
	extraCharge?: number;
	extraChargeType?: ExtraChargeType;
};

export const getEmptyConfiguratorData = (): Configurator => {
	return {
		sessionId: 0,
		electricityConsumption: {
			currentPrice: 0.35,
			duration: 20,
			inflation: 3,
			priceIncreaseRate: 3,
			electricityConsumption: 0
		},
		characteristicsConsumption: {
			characteristicsConsumption: CharacteristicsConsumptionEnum.EMPTY
		},
		roofSurfaces: {
			modules: []
		},
		insolation: {
			plz: '',
			location: '',
			source: '',
			insolation: 0,
			roofData: undefined,
			roofImageUrl: undefined
		},
		moduleComponent: {
			moduleType: undefined,
			batterySystem: undefined
		},
		additionOptions: [],
		acConnection: 1,
		dimensionFactorPV: 2,
		dimensionFactorBattery: 0.9,
		pvSize: 0,
		amountOfModule: 0,
		hasTax: false,
		discount: 0,
		amountOfBattery: 0,
		fuelPrice: 0,
		heatPumpConsume: 0,
		eCarConsume: 0,
		acConnectionPrice: 0
	};
};

const configuratorStore = writable<Configurator>(getEmptyConfiguratorData());

export const useConfiguratorStore = (initialValues?: Configurator) => {
	if (initialValues) {
		configuratorStore.set(initialValues);
	}

	const setConfiguratorStore = () => {
		setContext(StoreKeys.CONFIGURATOR, configuratorStore);
	};

	return {
		setConfiguratorStore,
		...configuratorStore
	};
};

export const getConfiguratorStore = (): Writable<Configurator> => {
	return getContext(StoreKeys.CONFIGURATOR);
};
